import type { QueryClient } from '@tanstack/react-query'

import { marketEndpoints } from '@/api/endpointCache'
import { type MoversParams, type MoversSort } from '@/features/market/api/marketApi'
import { backendSectorKey } from '@/features/market/api/marketMappers'
import { sortTableRows } from '@/lib/tableSorting'
import {
  moverBrokerRowSchema,
  moverRowSchema,
  type MoverBrokerRow,
  type MoverRow,
  type MoversQuery,
} from '@/schemas/movers'
import { sliceTablePage, tablePageSchema, type TablePage, type TableQuery } from '@/schemas/table'

/**
 * Table column id → the name mm-be sorts by. The two vocabularies differ on
 * three of the seven columns, and a column sent under the table's own name is
 * a 400 rather than a wrong order, so this map is the only place either name
 * may be spelled. A column absent from it (a hand-edited `?sortBy=`) sends no
 * `sort` at all, which leaves the tab's own ordering in place.
 *
 * `company` now orders by ticker, because that is the column mm-be sorts on;
 * it used to order by company name here. Both are the Company column, and only
 * the ticker ordering can be asked of the endpoint.
 */
const SORT_COLUMNS: Record<string, MoversSort> = {
  company: 'company',
  ltp: 'price',
  high: 'high',
  open: 'open',
  low: 'low',
  volumeKitta: 'volume',
  percentChange: 'change',
}

const sortParams = (query: MoversQuery): Pick<MoversParams, 'sort' | 'order'> => {
  const sort = query.sortBy === undefined ? undefined : SORT_COLUMNS[query.sortBy]
  return sort === undefined ? {} : { sort, order: query.sortDesc ? 'desc' : 'asc' }
}

const moversPageSchema = tablePageSchema(moverRowSchema)
const brokersPageSchema = tablePageSchema(moverBrokerRowSchema)

/** `/market/brokers/`'s own ceiling — `limit` is `max_value=50` (mm-be `BrokersRequestSerializer`). */
const BROKERS_LIMIT = 50

export const moversService = {
  /**
   * One page of the table, ordered and sliced by mm-be.
   *
   * Sorting and paging are the endpoint's since 2026-09-15 (`sort`/`order`/
   * `page`/`page_size`), so the whole market is ranked before the slice is
   * taken. Sorting a 100-row `limit=` fetch here instead ranked the top
   * hundred of the tab and called it the market — page 4 of Volume could not
   * exist, and "sort by price" meant the cheapest of the hundred busiest.
   *
   * `limit` is deliberately not sent: mm-be reads it as the Overview-preview
   * alias and pins the response to page 1.
   *
   * Sector is the only filter sent. mm-be still accepts `price_min`,
   * `price_max` and `turnover_min`, and this once mapped two band selects onto
   * them; V2 draws one sector select on the tab row and nothing else, so there
   * is no longer a control that could ask for a band.
   */
  table: async (query: MoversQuery, client?: QueryClient): Promise<TablePage<MoverRow>> => {
    const movers = await marketEndpoints(client).movers({
      type: query.view,
      page: query.page,
      page_size: query.pageSize,
      ...sortParams(query),
      ...(query.sector === undefined ? {} : { sector: backendSectorKey(query.sector) }),
    })
    const rows = movers.rows.map((row) => ({
      symbol: row.key,
      name: row.name,
      ltp: row.ltp,
      high: row.high,
      open: row.open,
      low: row.low,
      volumeKitta: row.volume,
      turnoverRs: row.turnover,
      transactions: row.transactions,
      percentChange: row.change_percent,
    }))

    return moversPageSchema.parse({
      rows,
      page: query.page,
      pageSize: query.pageSize,
      // The pager counts the whole filtered set, not this page. Falling back to
      // the row count would collapse the table to one page if meta ever lost
      // `total`, which is the safe direction: no phantom pages.
      total: movers.meta?.total ?? rows.length,
    })
  },

  /**
   * The Brokers tab: the session's busiest brokers by what they bought, sold and netted.
   *
   * `/market/brokers/` takes a `limit` and nothing else — no page, no sort — and answers at most
   * fifty, already ranked by turnover. So the whole list is fetched once and the member's sort and
   * page are applied here, over every row, before the slice: sorting one page of it would call a
   * page's biggest net buyer the day's.
   */
  brokers: async (query: TableQuery, client?: QueryClient): Promise<TablePage<MoverBrokerRow>> => {
    const { rows } = await marketEndpoints(client).brokers(BROKERS_LIMIT)
    const brokers = rows.map((row) => ({
      code: row.code,
      name: row.name ?? `Broker ${row.code}`,
      boughtKitta: row.buy_qty,
      soldKitta: row.sell_qty,
      netKitta: row.net_qty,
    }))
    const sorted = sortTableRows(brokers, query, {
      broker: (row) => row.name,
      bought: (row) => row.boughtKitta,
      sold: (row) => row.soldKitta,
      netKitta: (row) => row.netKitta,
    })
    return brokersPageSchema.parse(sliceTablePage(sorted, query))
  },
}
